<script setup lang="ts">
  import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { MaterialArrowUploadProgressFillIcon, MaterialCancelFillIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
  import { ChevronLeftIcon, ChevronRightIcon, File04Icon, Trash01Icon } from '@gohighlevel/ghl-icons/24/outline'
  import { AnalyticsEvents } from '@common/analytics-layer/types'
  import { createElementId } from '@common/core-layer/utils'
  import { formatVoiceDuration } from '@common/media-layer/utils/voice-duration'
  import CPSpin from '@ui/components/CPSpin.vue'
  import CPText from '@ui/components/CPText.vue'
  import type { SourceInfo } from '@common/media-layer/types'
  import type { VoiceNoteFields, VoiceNoteKind } from '@common/media-layer/types/voice-note.types'

  const OptimisedVideo = defineAsyncComponent(() => import('@common/media-layer/components/OptimisedVideo.vue'))
  const MediaGalleryModal = defineAsyncComponent(() => import('@common/media-layer/components/MediaGalleryModal.vue'))
  const OptimisedImage = defineAsyncComponent(() => import('@common/media-layer/components/OptimisedImage.vue'))

  const props = defineProps({
    images: {
      type: Array as PropType<ImageItem[]>,
      default: () => [],
    },
    // App source for DRM URL generation. Defaults to 'communities'.
    source: {
      type: String as PropType<'communities' | 'courses'>,
      default: 'communities',
    },
    videos: {
      type: Array as PropType<VideoItem[]>,
      default: () => [],
    },
    gifs: {
      type: Array as PropType<GifItem[]>,
      default: () => [],
    },
    files: {
      type: Array as PropType<FileItem[]>,
      default: () => [],
    },
    audio: {
      type: Array as PropType<AudioItem[]>,
      default: () => [],
    },
    variant: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: 'lg',
    },
    useRawUrl: {
      type: Boolean,
      default: false,
    },
    // Group the attachment belongs to, when there is one. Communities passes it so media_removed can
    // be joined against media_uploaded per group; courses has no group and correctly omits it.
    groupId: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
  })

  const emit = defineEmits<{
    removeImage: [index: number]
    removeVideo: [index: number]
    removeGif: [index: number]
    removeFile: [index: number]
    removeAudio: [index: number]
  }>()

  const { t } = useI18n()

  /**
   * Declarative GA binding for attachment removal. The capture-phase click listener pairs this
   * with the delete control's element id, so removal intent is measured at click time rather than
   * from a mutation outcome (see docs/ANALYTICS_ARCHITECTURE.md).
   */
  const mediaRemovedTrack = (mediaType: string) => ({
    event: AnalyticsEvents.MEDIA_REMOVED,
    type: mediaType,
    // groupId is not part of the enriched analytics context, so it only reaches GA if passed here.
    // Omitted rather than sent empty when absent, so "no group" stays distinguishable from "unknown".
    ...(props.groupId ? { groupId: props.groupId } : {}),
  })
  const carouselRef = ref<HTMLElement | null>(null)
  const canScrollLeft = ref(false)
  const canScrollRight = ref(false)
  const showGallery = ref(false)
  const galleryInitialIndex = ref(0)
  const galleryAttachments = computed(() => {
    const items: Array<{
      type: 'image' | 'video' | 'gif' | 'audio'
      url: string
      id?: string
      thumbnail?: string
      alt?: string
      duration?: number
      licenseId?: string
      assetType?: string
      status?: string
      processedUrl?: string
      originalUrl?: string
      sourceInfo?: SourceInfo
      uploading?: boolean
      error?: string
      progress?: number
      title?: string
      name?: string
      waveformPeaks?: number[]
      kind?: VoiceNoteKind
    }> = []

    // Add images
    props.images?.forEach(img => {
      items.push({
        type: 'image',
        url: img.url,
        id: img.id,
        thumbnail: img.url,
        alt: 'Image',
        licenseId: img.licenseId,
        uploading: img.uploading,
        error: img.error,
        progress: img.progress,
      })
    })

    // Add videos
    props.videos?.forEach(video => {
      items.push({
        type: 'video',
        url: video.url,
        id: video.id,
        thumbnail: video.thumbnail || '', // Use original thumbnail or empty for YouTube/Vimeo
        duration: video.duration,
        alt: video.file?.name || 'Video',
        licenseId: video.licenseId,
        assetType: video.assetType,
        status: video.status,
        processedUrl: video.processedUrl,
        originalUrl: video.originalUrl,
        sourceInfo: undefined, // SourceInfo not needed for gallery display
        uploading: video.uploading,
        error: video.error,
        progress: video.progress,
      })
    })

    // Add gifs
    props.gifs?.forEach(gif => {
      items.push({
        type: 'gif',
        url: gif.url,
        id: gif.id,
        thumbnail: gif.url,
        alt: gif.title || 'GIF',
        uploading: gif.uploading,
        error: gif.error,
        progress: gif.progress,
      })
    })

    props.audio?.forEach(item => {
      items.push({
        type: 'audio',
        url: item.url,
        id: item.id,
        duration: item.duration,
        alt: item.name || t('common.gallery.audio'),
        name: item.name,
        waveformPeaks: item.waveformPeaks,
        kind: item.kind,
        uploading: item.uploading,
        error: item.error,
        title: item.name,
      })
    })

    return items
  })
  const openGallery = (type: 'image' | 'video' | 'gif' | 'audio', index: number) => {
    // Calculate the index in the flattened array
    let flatIndex = 0

    if (type === 'image') {
      flatIndex = index
    } else if (type === 'video') {
      flatIndex = (props.images?.length || 0) + index
    } else if (type === 'gif') {
      flatIndex = (props.images?.length || 0) + (props.videos?.length || 0) + index
    } else if (type === 'audio') {
      flatIndex = (props.images?.length || 0) + (props.videos?.length || 0) + (props.gifs?.length || 0) + index
    }

    galleryInitialIndex.value = flatIndex
    showGallery.value = true
  }
  const checkScrollability = () => {
    if (!carouselRef.value) return

    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.value

    // Add a small threshold (2px) to account for sub-pixel rendering
    canScrollLeft.value = scrollLeft > 2
    canScrollRight.value = scrollLeft + clientWidth < scrollWidth - 2
  }
  const scrollLeft = () => {
    if (!carouselRef.value || !canScrollLeft.value) return
    const itemWidth = sizeConfig.value.width + 8 // width + margin
    carouselRef.value.scrollBy({ left: -itemWidth * 2, behavior: 'smooth' })

    // Re-check after scroll animation
    setTimeout(() => checkScrollability(), 350)
  }
  const scrollRight = () => {
    if (!carouselRef.value || !canScrollRight.value) return
    const itemWidth = sizeConfig.value.width + 8 // width + margin
    carouselRef.value.scrollBy({ left: itemWidth * 2, behavior: 'smooth' })

    // Re-check after scroll animation
    setTimeout(() => checkScrollability(), 350)
  }
  const sizeConfig = computed(() => {
    switch (props.variant) {
      case 'sm':
        return {
          width: 60,
          height: 60,
          containerClass: 'w-15 h-15',
          trashIcon: 'w-3 h-3 trash-icon',
          text: 'text-xs',
        }
      case 'md':
        return {
          width: 100,
          height: 100,
          containerClass: 'w-25 h-25',
          trashIcon: 'w-4 h-4 trash-icon',
          text: 'text-sm',
        }
      case 'lg':
      default:
        return {
          width: 160,
          height: 160,
          containerClass: 'w-40 h-40',
          trashIcon: 'w-5 h-5 trash-icon',
          text: 'text-base',
        }
    }
  })
  const totalItems = computed(
    () =>
      (props.images?.length || 0) +
      (props.videos?.length || 0) +
      (props.gifs?.length || 0) +
      (props.files?.length || 0) +
      (props.audio?.length || 0)
  )
  const uploadedItems = computed(() => {
    const uploadedImages = props.images?.filter(img => !img.uploading && !img.error && img.id && !img.id.startsWith('temp-')).length || 0
    const uploadedVideos =
      props.videos?.filter(video => !video.uploading && !video.error && video.id && !video.id.startsWith('temp-')).length || 0
    const uploadedGifs = props.gifs?.filter(gif => !gif.uploading && !gif.error && gif.id).length || 0
    const uploadedFiles = props.files?.filter(file => !file.uploading && !file.error && file.id && !file.id.startsWith('temp-')).length || 0
    const uploadedAudio = props.audio?.filter(item => !item.uploading && !item.error && item.id && !item.id.startsWith('temp-')).length || 0
    return uploadedImages + uploadedVideos + uploadedGifs + uploadedFiles + uploadedAudio
  })
  const hasAnyItems = computed(() => totalItems.value > 0)
  const getItemPriority = (item: UploadableItem) => {
    // Processed (uploaded successfully) = 1 (highest priority - shown first)
    if (!item.uploading && !item.error && item.id && !item.id.startsWith('temp-')) {
      return 1
    }
    // Processing (currently uploading) = 2
    if (item.uploading) {
      return 2
    }
    // Queued (waiting to upload) = 3
    if (!item.uploading && !item.error && (!item.id || item.id.startsWith('temp-'))) {
      return 3
    }
    // Error = 4 (lowest priority)
    if (item.error) {
      return 4
    }
    return 5 // fallback
  }
  const sortedAllItems = computed(() => {
    const allItems: Array<{
      item: ImageItem | VideoItem | GifItem | FileItem | AudioItem
      type: 'image' | 'video' | 'gif' | 'file' | 'audio'
      originalIndex: number
    }> = []

    // Add all images with metadata
    props.images?.forEach((img, index) => {
      allItems.push({ item: img, type: 'image', originalIndex: index })
    })

    // Add all videos with metadata
    props.videos?.forEach((video, index) => {
      allItems.push({ item: video, type: 'video', originalIndex: index })
    })

    // Add all gifs with metadata
    props.gifs?.forEach((gif, index) => {
      allItems.push({ item: gif, type: 'gif', originalIndex: index })
    })

    // Add all files with metadata
    props.files?.forEach((file, index) => {
      allItems.push({ item: file, type: 'file', originalIndex: index })
    })

    props.audio?.forEach((item, index) => {
      allItems.push({ item, type: 'audio', originalIndex: index })
    })

    // Sort by priority
    return allItems.sort((a, b) => getItemPriority(a.item) - getItemPriority(b.item))
  })
  const getFileIcon = (_fileType: string) => {
    // Use the same generic file icon for all document types for consistency
    return File04Icon
  }
  const attachmentFileName = (item: FileItem) => item.name || ''
  const attachmentFileStem = (item: FileItem) => {
    const name = attachmentFileName(item)
    return name.split('.').slice(0, -1).join('.') || name
  }
  const attachmentFileExt = (item: FileItem) => attachmentFileName(item).split('.').pop() || 'unknown'

  onMounted(() => {
    nextTick(() => {
      checkScrollability()

      // Setup ResizeObserver to check scrollability on container resize
      if (carouselRef.value) {
        const resizeObserver = new ResizeObserver(() => {
          checkScrollability()
        })
        resizeObserver.observe(carouselRef.value)

        // Cleanup on unmount
        onUnmounted(() => {
          resizeObserver.disconnect()
        })
      }
    })
  })

  interface UploadableItem {
    url: string
    uploading?: boolean
    error?: string
    id?: string
    progress?: number
  }
  interface ImageItem extends UploadableItem {
    file?: File
    // DRM-specific fields
    licenseId?: string
    assetType?: string
    status?: string
    processedUrl?: string
    originalUrl?: string
  }
  interface VideoItem {
    url: string
    thumbnail?: string
    duration?: number
    file?: File
    uploading?: boolean
    error?: string
    id?: string
    progress?: number
    // DRM-specific fields
    licenseId?: string
    assetType?: string
    status?: string
    processedUrl?: string
    originalUrl?: string
  }
  interface FileItem {
    url: string
    name: string
    type: string
    size: number
    file?: File
    uploading?: boolean
    error?: string
    id?: string
    progress?: number
  }
  interface AudioItem extends FileItem, VoiceNoteFields {}
  interface GifItem {
    url: string
    title?: string
    id?: string
    uploading?: boolean
    error?: string
    progress?: number
  }
  watch(
    [() => props.images, () => props.videos, () => props.gifs, () => props.files, () => props.audio],
    () => {
      nextTick(() => {
        checkScrollability()
      })
    },
    { deep: true }
  )
</script>

<template>
  <div v-if="hasAnyItems" class="relative border-t border-default px-2 py-3">
    <!-- Upload Progress Counter -->
    <div class="mb-3 flex items-center justify-between">
      <CPText :id="createElementId('editor', 'attachments', 'counter')" size="lg" weight="medium" class="text-700">
        {{ t('editor.attachmentsCount', { uploaded: uploadedItems, total: totalItems }) }}
      </CPText>

      <!-- Navigation Chevrons -->
      <div class="flex items-center gap-1">
        <div :id="createElementId('editor', 'attachments', 'chevron-left-button')" :disabled="!canScrollLeft">
          <ChevronLeftIcon
            class="h-5 w-5 transition-all"
            :class="canScrollLeft ? 'cursor-pointer text-600 hover:text-body' : 'cursor-not-allowed text-disabled'"
            @click="scrollLeft"
          />
        </div>
        <div :id="createElementId('editor', 'attachments', 'chevron-right-button')" :disabled="!canScrollRight">
          <ChevronRightIcon
            class="h-5 w-5 transition-all"
            :class="canScrollRight ? 'cursor-pointer text-600 hover:text-body' : 'cursor-not-allowed text-disabled'"
            @click="scrollRight"
          />
        </div>
      </div>
    </div>

    <!-- Carousel Container -->
    <div
      :id="createElementId('post-editor', 'upload-carousel', 'container')"
      ref="carouselRef"
      class="scrollbar-hide flex gap-2 overflow-x-auto scroll-smooth"
      @scroll="checkScrollability"
    >
      <!-- All Items Sorted -->
      <template v-for="(wrapper, index) in sortedAllItems">
        <!-- Image -->
        <div v-if="wrapper.type === 'image'" :key="`image-${wrapper.item.id || wrapper.originalIndex}`" class="relative flex-shrink-0">
          <div :class="[sizeConfig.containerClass, 'relative overflow-hidden rounded']" style="border-radius: 4px">
            <div
              :id="createElementId('editor', 'attachments', `delete-button-${index + 1}`)"
              v-track="mediaRemovedTrack('image')"
              role="button"
              tabindex="0"
              class="trash-icon-wrapper absolute right-2 top-2 z-20 flex cursor-pointer items-center justify-center rounded-full bg-background p-1 shadow-md transition-all hover:bg-background"
              @click.stop="emit('removeImage', wrapper.originalIndex)"
              @keydown.enter.stop="emit('removeImage', wrapper.originalIndex)"
              @keydown.space.prevent.stop="emit('removeImage', wrapper.originalIndex)"
            >
              <Trash01Icon size="sm" :class="sizeConfig.trashIcon" />
            </div>
            <!-- Pending/Queue overlay -->
            <div
              v-if="!wrapper.item.uploading && !wrapper.item.error && !(wrapper.item as ImageItem).licenseId && !wrapper.item.id"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <MaterialArrowUploadProgressFillIcon class="h-6 w-6 text-on-primary" />
              <div :class="['px-1 text-center text-on-primary', sizeConfig.text]">{{ t('editor.queued') }}</div>
            </div>

            <!-- Loading overlay with spinner + progress -->
            <div
              v-if="wrapper.item.uploading"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <CPSpin
                :id="`image-upload-spinner-${index}`"
                :size="variant === 'sm' ? 'sm' : variant === 'md' ? 'md' : 'lg'"
                color="white"
              />
              <div
                v-if="wrapper.item.progress && wrapper.item.progress > 0"
                :class="['mt-1 px-1 text-center text-on-primary', sizeConfig.text]"
              >
                {{ Math.round(wrapper.item.progress) }}%
              </div>
            </div>

            <!-- Error overlay -->
            <div
              v-if="wrapper.item.error"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-[var(--cp-danger-primary)]/90"
              :title="wrapper.item.error"
            >
              <MaterialCancelFillIcon class="h-6 w-6 text-on-primary" />
              <div :class="['px-1 text-center text-on-primary', sizeConfig.text]">{{ t('editor.failed') }}</div>
            </div>

            <!-- OptimisedImage with preview disabled - parent handles gallery -->
            <div
              :id="createElementId('editor', 'attachments', `image-${index + 1}`)"
              role="button"
              tabindex="0"
              class="relative z-0 h-full w-full cursor-pointer"
              @click="openGallery('image', wrapper.originalIndex)"
              @keydown.enter="openGallery('image', wrapper.originalIndex)"
              @keydown.space.prevent="openGallery('image', wrapper.originalIndex)"
            >
              <OptimisedImage
                :license-id="(wrapper.item as ImageItem).licenseId"
                :src="!(wrapper.item as ImageItem).licenseId ? wrapper.item.url : ''"
                :alt="`Image ${index + 1}`"
                :source="props.source"
                :preview="false"
                :contain="false"
                :use-gradient-background="true"
                img-class="object-cover"
                :max-width="sizeConfig.width"
                :max-height="sizeConfig.height"
              />
            </div>
          </div>
        </div>

        <!-- Video -->
        <div v-else-if="wrapper.type === 'video'" :key="`video-${wrapper.item.id || wrapper.originalIndex}`" class="relative flex-shrink-0">
          <div :class="[sizeConfig.containerClass, 'relative overflow-hidden rounded bg-[var(--cp-font-900)]']" style="border-radius: 4px">
            <div
              :id="createElementId('editor', 'attachments', `delete-button-${index + 1}`)"
              v-track="mediaRemovedTrack('video')"
              role="button"
              tabindex="0"
              class="trash-icon-wrapper absolute right-2 top-2 z-20 flex cursor-pointer items-center justify-center rounded-full bg-background p-1 shadow-md transition-all hover:bg-background"
              @click.stop="emit('removeVideo', wrapper.originalIndex)"
              @keydown.enter.stop="emit('removeVideo', wrapper.originalIndex)"
              @keydown.space.prevent.stop="emit('removeVideo', wrapper.originalIndex)"
            >
              <Trash01Icon size="sm" :class="sizeConfig.trashIcon" />
            </div>
            <!-- Pending/Queue overlay -->
            <div
              v-if="!wrapper.item.uploading && !wrapper.item.error && !(wrapper.item as VideoItem).licenseId && !wrapper.item.id"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <MaterialArrowUploadProgressFillIcon class="h-6 w-6 text-on-primary" />
              <div :class="['px-1 text-center text-on-primary', sizeConfig.text]">{{ t('editor.queued') }}</div>
            </div>

            <!-- Loading overlay with spinner + progress -->
            <div
              v-if="wrapper.item.uploading"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <CPSpin
                :id="`video-upload-spinner-${index}`"
                :size="variant === 'sm' ? 'sm' : variant === 'md' ? 'md' : 'lg'"
                color="white"
              />
              <div
                v-if="wrapper.item.progress && wrapper.item.progress > 0"
                :class="['mt-1 px-1 text-center text-on-primary', sizeConfig.text]"
              >
                {{ Math.round(wrapper.item.progress) }}%
              </div>
            </div>

            <!-- Error overlay -->
            <div
              v-if="wrapper.item.error"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-[var(--cp-danger-primary)]/90"
              :title="wrapper.item.error"
            >
              <MaterialCancelFillIcon class="h-6 w-6 text-on-primary" />
              <div :class="['px-1 text-center text-on-primary', sizeConfig.text]">{{ t('editor.failed') }}</div>
            </div>

            <div
              :id="createElementId('editor', 'attachments', `video-${index + 1}`)"
              role="button"
              tabindex="0"
              class="relative z-0 h-full w-full cursor-pointer"
              @click="openGallery('video', wrapper.originalIndex)"
              @keydown.enter="openGallery('video', wrapper.originalIndex)"
              @keydown.space.prevent="openGallery('video', wrapper.originalIndex)"
            >
              <OptimisedVideo
                :src="wrapper.item.url"
                :thumbnail="(wrapper.item as VideoItem).thumbnail"
                :alt="`Video ${index + 1}`"
                :duration="(wrapper.item as VideoItem).duration"
                :show-file-name="!!(wrapper.item as VideoItem).file?.name"
                :width="`${sizeConfig.width}px`"
                :height="`${sizeConfig.height}px`"
                class="pointer-events-none object-cover w-full h-full"
                :thumbnail-only="true"
                :open-in-gallery="false"
                :license-id="(wrapper.item as VideoItem).licenseId"
              />
            </div>
          </div>
        </div>

        <!-- GIF -->
        <div v-else-if="wrapper.type === 'gif'" :key="`gif-${wrapper.item.id || wrapper.originalIndex}`" class="relative flex-shrink-0">
          <div :class="[sizeConfig.containerClass, 'relative overflow-hidden rounded']" style="border-radius: 4px">
            <div
              :id="createElementId('editor', 'attachments', `delete-button-${index + 1}`)"
              v-track="mediaRemovedTrack('gif')"
              role="button"
              tabindex="0"
              class="trash-icon-wrapper absolute right-2 top-2 z-20 flex cursor-pointer items-center justify-center rounded-full bg-background p-1 shadow-md transition-all hover:bg-background"
              @click.stop="emit('removeGif', wrapper.originalIndex)"
              @keydown.enter.stop="emit('removeGif', wrapper.originalIndex)"
              @keydown.space.prevent.stop="emit('removeGif', wrapper.originalIndex)"
            >
              <Trash01Icon size="sm" :class="sizeConfig.trashIcon" />
            </div>
            <!-- Loading overlay with spinner + progress -->
            <div
              v-if="wrapper.item.uploading"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <CPSpin :id="`gif-upload-spinner-${index}`" :size="variant === 'sm' ? 'sm' : variant === 'md' ? 'md' : 'lg'" color="white" />
              <div
                v-if="wrapper.item.progress && wrapper.item.progress > 0"
                :class="['mt-1 px-1 text-center text-on-primary', sizeConfig.text]"
              >
                {{ Math.round(wrapper.item.progress) }}%
              </div>
            </div>

            <!-- Error overlay -->
            <div
              v-if="wrapper.item.error"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-[var(--cp-danger-primary)]/90"
              :title="wrapper.item.error"
            >
              <MaterialCancelFillIcon class="h-6 w-6 text-on-primary" />
              <div :class="['px-1 text-center text-on-primary', sizeConfig.text]">{{ t('editor.failed') }}</div>
            </div>

            <!-- OptimisedImage for GIFs with preview disabled - parent handles gallery -->
            <div
              role="button"
              tabindex="0"
              class="relative z-0 h-full w-full cursor-pointer"
              @click="openGallery('gif', wrapper.originalIndex)"
              @keydown.enter="openGallery('gif', wrapper.originalIndex)"
              @keydown.space.prevent="openGallery('gif', wrapper.originalIndex)"
            >
              <OptimisedImage
                :src="wrapper.item.url"
                :alt="(wrapper.item as GifItem).title || `GIF ${index + 1}`"
                :preview="false"
                rounded="rounded"
                :contain="false"
                :use-gradient-background="false"
                img-class="object-cover"
                :max-width="sizeConfig.width"
                :max-height="sizeConfig.height"
              />
            </div>
          </div>
        </div>

        <!-- File -->
        <div v-else-if="wrapper.type === 'file'" :key="`file-${wrapper.item.id || wrapper.originalIndex}`" class="relative flex-shrink-0">
          <div
            :id="createElementId('file', 'attachment', attachmentFileExt(wrapper.item as FileItem))"
            :class="[
              sizeConfig.containerClass,
              'file-attachment-container',
              'relative flex flex-col items-center justify-center rounded gap-1 px-2 py-2',
            ]"
          >
            <div
              :id="createElementId('editor', 'attachments', `delete-button-${index + 1}`)"
              v-track="mediaRemovedTrack('document')"
              role="button"
              tabindex="0"
              class="trash-icon-wrapper absolute right-2 top-2 z-20 flex cursor-pointer items-center justify-center rounded-full bg-background p-1 shadow-md transition-all hover:bg-background"
              @click.stop="emit('removeFile', wrapper.originalIndex)"
              @keydown.enter.stop="emit('removeFile', wrapper.originalIndex)"
              @keydown.space.prevent.stop="emit('removeFile', wrapper.originalIndex)"
            >
              <Trash01Icon size="sm" :class="sizeConfig.trashIcon" />
            </div>
            <!-- Pending/Queue overlay -->
            <div
              v-if="!wrapper.item.uploading && !wrapper.item.error && !wrapper.item.id"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-overlay-dark"
            >
              <MaterialArrowUploadProgressFillIcon class="h-6 w-6 text-on-primary" />
              <CPText
                :size="sizeConfig.text === 'text-xs' ? 'xs' : sizeConfig.text === 'text-sm' ? 'sm' : 'md'"
                class="px-1 text-center text-on-primary"
              >
                {{ t('editor.queued') }}
              </CPText>
            </div>

            <!-- Loading overlay with spinner + progress -->
            <div
              v-if="wrapper.item.uploading"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-overlay-dark"
            >
              <CPSpin :id="`file-upload-spinner-${index}`" :size="variant === 'sm' ? 'sm' : variant === 'md' ? 'md' : 'lg'" color="white" />
              <div
                v-if="wrapper.item.progress && wrapper.item.progress > 0"
                :class="['mt-1 px-1 text-center text-on-primary', sizeConfig.text]"
              >
                {{ Math.round(wrapper.item.progress) }}%
              </div>
            </div>

            <!-- Error overlay -->
            <div
              v-if="wrapper.item.error"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-[var(--cp-danger-primary)]/90"
              :title="wrapper.item.error"
            >
              <MaterialCancelFillIcon class="h-6 w-6 text-on-primary" />
              <CPText
                :size="sizeConfig.text === 'text-xs' ? 'xs' : sizeConfig.text === 'text-sm' ? 'sm' : 'md'"
                class="px-1 text-center text-on-primary"
              >
                {{ t('editor.failed') }}
              </CPText>
            </div>

            <!-- File Icon -->
            <div
              class="flex flex-shrink-0 items-center justify-center rounded-full bg-primary-50"
              :class="sizeConfig.text === 'text-xs' ? 'h-6 w-6' : sizeConfig.text === 'text-sm' ? 'h-8 w-8' : 'h-9 w-9'"
            >
              <component
                :is="getFileIcon((wrapper.item as FileItem).type || '')"
                :class="sizeConfig.text === 'text-xs' ? 'h-3.5 w-3.5' : sizeConfig.text === 'text-sm' ? 'h-4 w-4' : 'h-5 w-5'"
                class="text-primary"
              />
            </div>

            <!-- File Name -->
            <p class="file-name-text text-center">
              {{ attachmentFileStem(wrapper.item as FileItem) }}
            </p>

            <!-- File Type Badge -->
            <span class="file-type-badge uppercase">
              {{ attachmentFileExt(wrapper.item as FileItem) }}
            </span>
          </div>
        </div>

        <!-- Audio (voice note or uploaded clip) — never OptimisedVideo -->
        <div
          v-else-if="wrapper.type === 'audio'"
          :id="createElementId('editor', 'attachments', `audio-${index + 1}`)"
          :key="`audio-${wrapper.item.id || wrapper.originalIndex}`"
          role="button"
          tabindex="0"
          class="relative flex-shrink-0 cursor-pointer"
          @click="openGallery('audio', wrapper.originalIndex)"
          @keydown.enter="openGallery('audio', wrapper.originalIndex)"
          @keydown.space.prevent="openGallery('audio', wrapper.originalIndex)"
        >
          <div :class="[sizeConfig.containerClass, 'relative overflow-hidden rounded bg-surface']" style="border-radius: 4px">
            <div
              :id="createElementId('editor', 'attachments', `delete-button-${index + 1}`)"
              role="button"
              tabindex="0"
              class="trash-icon-wrapper absolute right-2 top-2 z-20 flex cursor-pointer items-center justify-center rounded-full bg-background p-1 shadow-md transition-all hover:bg-background"
              @click.stop="emit('removeAudio', wrapper.originalIndex)"
              @keydown.enter.stop="emit('removeAudio', wrapper.originalIndex)"
              @keydown.space.prevent.stop="emit('removeAudio', wrapper.originalIndex)"
            >
              <Trash01Icon size="sm" :class="sizeConfig.trashIcon" />
            </div>
            <div
              v-if="!wrapper.item.uploading && !wrapper.item.error && (!wrapper.item.id || wrapper.item.id.startsWith('temp-'))"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <MaterialArrowUploadProgressFillIcon class="h-6 w-6 text-on-primary" />
              <CPText
                :size="sizeConfig.text === 'text-xs' ? 'xs' : sizeConfig.text === 'text-sm' ? 'sm' : 'md'"
                class="px-1 text-center text-on-primary"
              >
                {{ t('editor.queued') }}
              </CPText>
            </div>
            <div
              v-if="wrapper.item.uploading"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-overlay-dark"
            >
              <CPSpin
                :id="`audio-upload-spinner-${index}`"
                :size="variant === 'sm' ? 'sm' : variant === 'md' ? 'md' : 'lg'"
                color="white"
              />
            </div>
            <div
              v-if="wrapper.item.error"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-[var(--cp-danger-primary)]/90"
              :title="wrapper.item.error"
            >
              <MaterialCancelFillIcon class="h-6 w-6 text-on-primary" />
              <div :class="['px-1 text-center text-on-primary', sizeConfig.text]">{{ t('editor.failed') }}</div>
            </div>
            <div class="relative z-0 h-full w-full">
              <slot name="audio" :item="wrapper.item" :index="index" :original-index="wrapper.originalIndex">
                <div class="flex h-full w-full flex-col items-center justify-center gap-1 px-2">
                  <File04Icon class="h-8 w-8 text-primary" />
                  <CPText size="sm" class="text-700">
                    {{ formatVoiceDuration((wrapper.item as AudioItem).duration || 0) }}
                  </CPText>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Media Gallery Modal -->
    <MediaGalleryModal
      :show="showGallery"
      :attachments="galleryAttachments"
      :initial-index="galleryInitialIndex"
      :allow-delete="false"
      :allow-download="false"
      :source="props.source"
      :use-raw-url="useRawUrl"
      @close="showGallery = false"
    />
  </div>
</template>

<style scoped>
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  /* Custom width classes for Tailwind */
  .w-15 {
    width: 3.75rem;
  }
  .h-15 {
    height: 3.75rem;
  }
  .w-25 {
    width: 6.25rem;
  }
  .h-25 {
    height: 6.25rem;
  }

  .trash-icon-wrapper :deep(svg) {
    stroke: rgb(239 68 68) !important;
    fill: none !important;
  }

  .file-attachment-container {
    border: 1px solid var(--cp-border-500);
    border-radius: 8px;
    background: var(--cp-surface-500);
  }

  .file-name-text {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;
    color: var(--cp-font-900);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    width: 100%;
    padding: 0 4px;
  }

  .file-type-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: var(--cp-primary-500);
    background: var(--cp-primary-50);
    line-height: 1.5;
  }

  :deep(.viewer-image) {
    aspect-ratio: 1/1;
  }
</style>
